// Cutting a clip in the browser (ADR-0058): keep frames [from, to] of a
// video, re-encode that range frame-accurately with the browser's own
// H.264 encoder (WebCodecs, through Mediabunny) and copy the AAC audio
// packets unchanged, so a browser that cannot encode AAC (Firefox) still
// keeps the sound. Our clips carry one keyframe, at frame 0, so a cut
// anywhere but the end could not be a plain stream copy anyway.
//
// The frame math is pure and tested here; `cutVideo` needs WebCodecs and
// is exercised in a browser, not in jsdom.

import { DEFAULT_FPS } from "./compare";

export interface CutRange {
  /** first kept frame, 0-based */
  from: number;
  /** last kept frame, inclusive */
  to: number;
}

/** The clip's frame count from its probed duration and rate. */
export function frameCount(durationS: number | undefined, fps: number | undefined): number {
  const rate = fps && fps > 0 ? fps : DEFAULT_FPS;
  if (!durationS || !Number.isFinite(durationS) || durationS <= 0) return 0;
  return Math.max(1, Math.round(durationS * rate));
}

/** The frame on screen at `time`: frame k covers [k/fps, (k+1)/fps). A
 * small epsilon keeps a seek to exactly k/fps on frame k despite float
 * error. */
export function frameAt(time: number, fps: number | undefined, frames: number): number {
  const rate = fps && fps > 0 ? fps : DEFAULT_FPS;
  if (!Number.isFinite(time) || time <= 0) return 0;
  const k = Math.floor(time * rate + 1e-6);
  return frames > 0 ? Math.min(k, frames - 1) : k;
}

/** Where to seek to show frame k: its middle, so the browser's decoder
 * lands on it rather than on the frame before. */
export function timeOfFrame(k: number, fps: number | undefined): number {
  const rate = fps && fps > 0 ? fps : DEFAULT_FPS;
  return (Math.max(0, k) + 0.5) / rate;
}

/** A usable range: inside the clip, at least two frames, and not the whole
 * clip unchanged. */
export function validCut(r: CutRange, frames: number): boolean {
  return (
    Number.isInteger(r.from) &&
    Number.isInteger(r.to) &&
    r.from >= 0 &&
    r.to < frames &&
    r.to - r.from >= 1 &&
    !(r.from === 0 && r.to === frames - 1)
  );
}

/** "kept 0:02.13 of 0:05.04 (51 of 121 frames)" style numbers. */
export function keptFrames(r: CutRange): number {
  return r.to - r.from + 1;
}

/** Seconds as m:ss.cc, for frame-level positions. */
export function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s.toFixed(2).padStart(5, "0")}`;
}

/** The new artifact's label and attrs: a result (so the Library's default
 * Results filter shows it) that names where it came from. */
export function cutMeta(
  source: { id: string; label?: string },
  r: CutRange,
): { label: string; attrs: Record<string, string | number> } {
  const base = source.label?.trim() || source.id.slice(0, 8);
  const label = `${base} · cut ${r.from}–${r.to}`.slice(0, 200);
  return { label, attrs: { role: "result", edit: "cut", source: source.id, cut_from: r.from, cut_to: r.to } };
}

export class CutError extends Error {}

/** H.264 quantizer for the re-encode, like ffmpeg's crf: lower is better.
 * An encoder without quantizer mode uses the source's bitrate instead;
 * headless Chrome's OpenH264 on Linux ignores both and lands near 4 Mbps,
 * still 44 dB PSNR against the source on a 896×1344 clip (2026-10-23). */
export const CUT_QUANTIZER = 18;

/** Whether this browser can encode H.264 at the clip's size. */
export async function canCut(width: number, height: number): Promise<boolean> {
  const { canEncodeVideo } = await import("mediabunny");
  return canEncodeVideo("avc", { width, height });
}

/**
 * Keep frames [r.from, r.to] of `source` (an MP4). The video is re-encoded
 * at near-lossless quality (a quantizer like ffmpeg's crf, with the
 * source's own bitrate as the fallback); audio packets are copied, from
 * the first one starting at or after the cut's start until its end (AAC
 * frames are ~21 ms, so the sound is within a frame of the picture).
 */
export async function cutVideo(
  source: Blob,
  r: CutRange,
  fps: number,
  onProgress?: (fraction: number) => void,
): Promise<Blob> {
  const mb = await import("mediabunny");
  const input = new mb.Input({ source: new mb.BlobSource(source), formats: mb.ALL_FORMATS });
  const video = await input.getPrimaryVideoTrack();
  if (!video) throw new CutError("the file has no video track");
  const audio = await input.getPrimaryAudioTrack();
  const rate = fps > 0 ? fps : DEFAULT_FPS;
  const t0 = r.from / rate;
  const t1 = (r.to + 1) / rate;
  const width = video.displayWidth;
  const height = video.displayHeight;
  if (!(await mb.canEncodeVideo("avc", { width, height }))) {
    throw new CutError(`this browser cannot encode H.264 at ${width}×${height}`);
  }
  const stats = await video.computePacketStats(120);
  const sourceBitrate = Math.max(2_000_000, Math.round(stats.averageBitrate || 0));

  const target = new mb.BufferTarget();
  const output = new mb.Output({ format: new mb.Mp4OutputFormat({ fastStart: "in-memory" }), target });
  const videoOut = new mb.VideoSampleSource({
    codec: "avc",
    quality: new mb.Quality({ quantizer: CUT_QUANTIZER, bitrate: sourceBitrate }),
    keyFrameInterval: 2,
  });
  output.addVideoTrack(videoOut, { frameRate: rate, rotation: video.rotation });
  const audioOut = audio ? new mb.EncodedAudioPacketSource(audio.codec as never) : undefined;
  if (audio && audioOut) output.addAudioTrack(audioOut);
  await output.start();

  const total = keptFrames(r);
  let done = 0;
  const sink = new mb.VideoSampleSink(video);
  // Ask for a half-frame margin on both sides and keep frames by index, so
  // float timestamps can neither drop the first frame nor add one.
  for await (const sample of sink.samples(Math.max(0, t0 - 0.5 / rate), t1 - 0.5 / rate)) {
    const k = Math.round(sample.timestamp * rate);
    if (k >= r.from && k <= r.to) {
      sample.setTimestamp((k - r.from) / rate);
      await videoOut.add(sample);
      done++;
      onProgress?.(done / total);
    }
    sample.close();
  }
  if (done !== total) {
    await output.cancel();
    throw new CutError(`decoded ${done} of ${total} frames`);
  }

  if (audio && audioOut) {
    const packets = new mb.EncodedPacketSink(audio);
    const config = await audio.getDecoderConfig();
    let first = true;
    for await (const p of packets.packets()) {
      if (p.timestamp < t0 - 1e-6 || p.timestamp >= t1) continue;
      await audioOut.add(p.clone({ timestamp: p.timestamp - t0 }), first && config ? { decoderConfig: config } : undefined);
      first = false;
    }
  }
  await output.finalize();
  if (!target.buffer) throw new CutError("the encoder produced no file");
  return new Blob([target.buffer], { type: "video/mp4" });
}
